import { useEffect, useState } from "react";

import {
  DataTreeViewItemValue,
  FlatNodeItem
} from "@components/common/tree-view/Interfaces";
import _ from "lodash";
import { useLang } from "@hooks/useLang";
import { HVIcon, HVText } from "@repo/HVComponents";
import { ObjectType } from "@queries/tanstack/analytics-filter/AnalyticsFilterQuery";
import { useLocationListByDeviceId } from "@hooks/analytics/useLocationListByDeviceId";
import { ANALYTICS_CHART_TYPE } from "@resources/Constants";
import { useAnalyticsRuleData } from "@hooks/analytics/useAnalyticsRuleData";
import { getRuleType } from "@hooks/analytics/useLocationRuleGroupByRuleType";
import * as S from "../LeftMenu.styled";
import { AnalyticsRuleLayer } from "../../rule/AnalyticsRuleLayer";
import { ICON } from "../../../../constants/icons";
import { MAX_LOCATION_COUNT } from "../../rule/AnalyticsRuleTree";

interface RuleButtonProps {
  ruleCategory: ObjectType;
  defaultRules: string[];
  onChange: (rule: FlatNodeItem[]) => void;
  disabled?: boolean;
  analyticsChartType?: string;
  setSelectedRules?: (rules: string[]) => void;
  importAnalytics?: boolean;
}
export const RuleButton = (props: RuleButtonProps) => {
  const { translate } = useLang();
  const [checkedRules, setCheckedRules] = useState<DataTreeViewItemValue[]>([]);
  const onCheckRule = (checkedNodes: FlatNodeItem[]) => {
    const rules = checkedNodes.filter((node) => !node.children);
    setCheckedRules(rules.map((node) => node.value));
  };
  const [isShowRuleLayer, setIsShowRuleLayer] = useState(false);
  const aiType =
    props.analyticsChartType &&
    (getRuleType(
      props.analyticsChartType as keyof typeof ANALYTICS_CHART_TYPE
    ) as string);

  const { data: analyticsRules } = useAnalyticsRuleData(
    {
      ruleCategory: props.ruleCategory,
      aiType
    },
    {
      refetchOnMount: false
    }
  );

  const { data: locationList } = useLocationListByDeviceId({
    deviceIds: _.uniq(
      (checkedRules as string[]).map((rule) => {
        const [_, deviceId] = (rule as string).split(":");
        return deviceId;
      })
    )
  });

  useEffect(() => {
    if (aiType && analyticsRules.length === 0 && props.importAnalytics) {
      props.setSelectedRules?.([]);
    }
  }, [aiType]);

  useEffect(() => {
    setCheckedRules(props.defaultRules);
  }, [props.defaultRules]);

  const isExceedLocationCount =
    locationList && locationList.length > MAX_LOCATION_COUNT;
  return (
    <>
      <S.MenuButton
        data-id="root"
        style={{ padding: "0 11px", minWidth: "102px", position: "relative" }}
        onClick={() => setIsShowRuleLayer(true)}
        disabled={props.disabled}
        data-testid="locations-selected"
      >
        <HVIcon
          name="location"
          svgProps={{ ...ICON.SIZE_20, ...ICON.COLOR_STROKE }}
          styleClass="icon-hover-effect"
        />
        {(locationList?.length ?? 0) > 0 || props.importAnalytics
          ? `${locationList && locationList.length} ${translate("selected")}`
          : `${translate("select")} ${translate("location")}`}
        {isShowRuleLayer && (
          <S.LayerWrap onClick={(e) => e.stopPropagation()}>
            <AnalyticsRuleLayer
              ruleCategory={props.ruleCategory}
              onOK={() => setIsShowRuleLayer(false)}
              defaultRules={checkedRules.map(
                (rule) => ({ value: rule }) as FlatNodeItem
              )}
              onChecked={(v) => {
                onCheckRule(v);
                props.onChange(v);
              }}
              analyticsChartType={props.analyticsChartType}
            />
          </S.LayerWrap>
        )}
      </S.MenuButton>
      {isExceedLocationCount && (
        <S.IconTextWrap>
          {/* error icon type is not defined yet in HVIcon, so use svg directly
            remove and change to the HVIcon after defined in HVIcon */}
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M1.5 8C1.5 4.41015 4.41015 1.5 8 1.5C11.5899 1.5 14.5 4.41015 14.5 8C14.5 11.5899 11.5899 14.5 8 14.5C4.41015 14.5 1.5 11.5899 1.5 8ZM8.5 5.33333C8.5 5.05719 8.27614 4.83333 8 4.83333C7.72386 4.83333 7.5 5.05719 7.5 5.33333L7.5 8C7.5 8.27614 7.72386 8.5 8 8.5C8.27614 8.5 8.5 8.27614 8.5 8L8.5 5.33333ZM8 9.5C7.53976 9.5 7.16667 9.8731 7.16667 10.3333C7.16667 10.7936 7.53976 11.1667 8 11.1667C8.46024 11.1667 8.83333 10.7936 8.83333 10.3333C8.83333 9.8731 8.46024 9.5 8 9.5Z"
              fill="#F65A5A"
            />
          </svg>
          <HVText type="Body-sm" weight="Regular" c="var(--hv-data-red)">
            {translate("analytics_selection_limit")}
          </HVText>
        </S.IconTextWrap>
      )}
    </>
  );
};
